import { motion } from "framer-motion";
import { Database, MessageSquare, CreditCard, Users } from "lucide-react";
import type { WorkflowStepType } from "./WorkflowBuilder";

interface ActionSelectorProps {
  onSelect: (step: WorkflowStepType) => void;
  onCancel: () => void;
}

const ActionSelector = ({ onSelect, onCancel }: ActionSelectorProps) => {
  const actions = [
    {
      type: "action" as const,
      title: "Check CRM Data",
      description: "Fetch customer information from Salesforce/HubSpot",
      icon: Users,
    },
    {
      type: "action" as const,
      title: "Check Payment Status",
      description: "Verify payment information in Stripe",
      icon: CreditCard,
    },
    {
      type: "action" as const,
      title: "Query Database",
      description: "Look up customer records in internal database",
      icon: Database,
    },
    {
      type: "condition" as const,
      title: "Ask for Information",
      description: "Request additional details from the customer",
      icon: MessageSquare,
    },
  ];

  const handleSelect = (action: typeof actions[0]) => {
    const step: WorkflowStepType = {
      ...action,
      id: crypto.randomUUID(), // Generate a unique ID for the step
    };
    onSelect(step);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border border-gray-200 p-4 space-y-2"
    >
      <h3 className="font-medium text-gray-900 mb-4">Select Next Action</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.title}
              onClick={() => handleSelect(action)}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-left border border-gray-100 hover:border-primary/30"
            >
              <div className="rounded-full p-2 bg-primary/10">
                <Icon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-sm text-gray-900">{action.title}</h4>
                <p className="text-xs text-gray-500">{action.description}</p>
              </div>
            </button>
          );
        })}
      </div>
      <button
        onClick={onCancel}
        className="text-sm text-gray-500 hover:text-gray-700 mt-4"
      >
        Cancel
      </button>
    </motion.div>
  );
};

export default ActionSelector;